import React, { ChangeEventHandler, FC, ReactElement, ReactNode } from 'react'

import {
  defineMessages,
  FormattedMessage,
  IntlShape,
  useIntl,
} from 'react-intl'

import {
  availableAges,
  availableWeight,
  availableHeight,
} from 'common/constants'
import { Nowrap, StaticSelect } from 'components/presentational/input'
import { RouterLink } from 'components/presentational/link'
import { useAuthorizationPath } from 'hooks/useAuthorizationLink'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

import { convertToFeet } from '../../../client/functions/convertToFeet'
import { convertToPound } from '../../../client/functions/convertToPound'

export const messages = defineMessages({
  heightPlural: {
    id: 'settings.height_plural',
    defaultMessage: '{height} см ({inches})',
  },
  weightPlural: {
    id: 'settings.weight_plural',
    defaultMessage: '{weight} кг ({pounds} lb)',
  },
})

export const UnitSelect: FC<{
  name: string
  value: string | number
  onChange: ChangeEventHandler
  filter: (value: number) => boolean
  renderLabel?: (intl, selectedChild) => ReactNode
  renderOption?: (intl, filter) => ReactNode
  nullOption?: ReactNode
  href?: string
  skip?: boolean
  disabled?: boolean
}> = ({
  name,
  value,
  onChange,
  filter = () => true,
  renderLabel = renderLabelDefault,
  renderOption = renderOptionDefault,
  nullOption,
  href,
  skip,
  disabled,
}) => {
  const intl = useIntl()

  const { authorized } = useShallowEqualSelector(
    ({ authorizationReducer: { authorized } }) => ({
      authorized,
    })
  )

  const authorizationPath = useAuthorizationPath()

  if (skip && !authorized) {
    return (
      <RouterLink data-name={name} to={authorizationPath}>
        {renderLabel(intl, value)}
      </RouterLink>
    )
  }

  if (href) {
    return (
      <RouterLink data-name={name} to={href}>
        {renderLabel(intl, value)}
      </RouterLink>
    )
  }

  return (
    <StaticSelect
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onChange}
      renderLabel={(value) => renderLabel(intl, value)}
      disabled={disabled}
    >
      {nullOption}
      {renderOption(intl, filter)}
    </StaticSelect>
  )
}

const renderLabelDefault = (intl, selectedChild) => selectedChild.props.value

export const renderLabelWeight = (intl, selectedChild): JSX.Element => {
  const unit =
    selectedChild && selectedChild.props
      ? selectedChild.props.value
      : selectedChild

  if (!unit) {
    return (
      <FormattedMessage
        id="dating_profile.form.not_specified"
        defaultMessage="Не указано"
      />
    )
  }

  return <Nowrap>{renderTextWeight(intl, unit)}</Nowrap>
}

export const renderLabelHeight = (
  intl: IntlShape,
  selectedChild
): ReactElement => {
  const unit =
    selectedChild && selectedChild.props
      ? selectedChild.props.value
      : selectedChild

  if (!unit) {
    return (
      <FormattedMessage
        id="dating_profile.form.not_specified"
        defaultMessage="Не указано"
      />
    )
  }

  return <Nowrap>{renderTextHeight(intl, unit)}</Nowrap>
}

const renderOptionDefault = (intl, filter) => {
  return availableAges.filter(filter).map((unit) => {
    return (
      <option key={unit} value={unit}>
        {unit}
      </option>
    )
  })
}

export const renderOptionHeight = (intl, filter) => {
  return availableHeight.filter(filter).map((unit) => {
    return (
      <option key={unit} value={unit}>
        {renderTextHeight(intl, unit)}
      </option>
    )
  })
}

export const renderOptionWeight = (intl, filter): ReactNode => {
  return availableWeight.filter(filter).map((unit) => {
    return (
      <option key={unit} value={unit}>
        {renderTextWeight(intl, unit)}
      </option>
    )
  })
}

// TODO: надо бы сюда и ниже как то то во внутрь занести intl чтобы не таскать
export const renderTextWeight = (intl, unit) => {
  return intl.formatMessage(messages.weightPlural, {
    weight: unit,
    pounds: convertToPound(unit),
  })
}

export const renderTextHeight = (intl, unit) => {
  return intl.formatMessage(messages.heightPlural, {
    height: unit,
    inches: convertToFeet(unit),
  })
}
